import React, { useContext } from "react";
import PropTypes from "prop-types";
import { grey2, grey90, black, zIndexHigherThanPlatform, setAppearanceConstant } from "@churchofjesuschrist/eden-style-constants";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import styled from "styled-components";
import Portal from "./components/Portal";
import { AppearanceContext, AppearanceSection } from "@churchofjesuschrist/eden-appearance";
import { ConditionalWrapper } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx } from "react/jsx-runtime";
const maskBackgroundLight = setAppearanceConstant("mask-background-light", {
  light: adjustHexAlpha(grey2, 0.6),
  dark: adjustHexAlpha(black, 0.6),
  prefix: "eden"
});
const maskBackgroundDark = setAppearanceConstant("mask-background-dark", {
  light: adjustHexAlpha(grey90, 0.87),
  dark: adjustHexAlpha(black, 0.8),
  prefix: "eden"
});

/**
 * The @supports is meant for progressive enhancement of the backdrop-filter.
 */
const Scrim = styled.div.withConfig({
  componentId: "sc-isi5s2-0"
})(["@supports (backdrop-filter:blur(2px)){backdrop-filter:blur( ", " );}background:", ";visibility:", ";top:0;right:0;bottom:0;left:0;position:fixed;pointer-events:auto;transition:background-color 100ms linear,backdrop-filter 100ms linear;z-index:", ";"], maskTypeIsLight => maskTypeIsLight ? "4px" : "2px", _ref => {
  let {
    maskTypeIsLight,
    active
  } = _ref;
  return maskTypeIsLight ? active ? maskBackgroundLight : adjustHexAlpha(grey2, 0) : active ? maskBackgroundDark : adjustHexAlpha(black, 0);
}, props => props.active ? "visible" : "hidden", zIndexHigherThanPlatform);
const PlatformMask = _ref2 => {
  let {
    active,
    children,
    type = "normal",
    ...props
  } = _ref2;
  const appearanceContext = useContext(AppearanceContext);
  // Because `Portal` is appended to the body, the resulting content is not a child of a potential `AppearanceSection` parent.  If we are inside an `AppearanceSection`, wrap the `Portal` in it's own `AppearanceSection` using the parent mode to ensure the correct mode is used.
  return /*#__PURE__*/_jsx(Portal, {
    children: /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: appearanceContext === null || appearanceContext === void 0 ? void 0 : appearanceContext.mode,
      wrapper: /*#__PURE__*/_jsx(AppearanceSection, {
        mode: appearanceContext === null || appearanceContext === void 0 ? void 0 : appearanceContext.mode,
        forceOutput: true
      }),
      children: /*#__PURE__*/_jsx(Scrim, {
        ...props,
        active: active,
        maskTypeIsLight: type === "media" ? false : true,
        "data-testid": "scrim",
        children: children
      })
    })
  });
};
PlatformMask.propTypes = {
  /** Content on top of masked overlay */
  children: PropTypes.node.isRequired,
  /** Whether masked overlay is displayed */
  active: PropTypes.bool,
  /** Receives a string to determine light or dark scrim "media" = dark "normal" = light */
  type: PropTypes.oneOf(["media", "normal"])
};
export default PlatformMask;